import { useState } from 'react'
import {
  type ApiTokenView,
  type MintedToken,
  scopeGroups,
  tokenActions,
  useAdminApiTokens,
  useCreateApiToken,
  useDeleteApiToken,
  useRevokeApiToken,
  useRotateApiToken,
  useUpdateApiTokenScopes,
} from '../../../features/admin/use-admin-api-tokens'
import { useAdminPermissions } from '../../../features/admin/use-admin-permissions'
import { AdminPage, adminError } from './admin-bits'
import { Button, ConfirmDialog, Dialog, EmptyRow, Field, Inline, inputCls, td, th } from './controls'
import { Badge, Card, mono } from './ui'

function ScopePicker({ selected, onChange }: { selected: string[]; onChange: (next: string[]) => void }) {
  const perms = useAdminPermissions()
  const groups = scopeGroups(perms.data?.permissions ?? [], selected)
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id])
  return (
    <div className="max-h-64 overflow-auto rounded-[10px] border border-[var(--color-border)] p-3">
      {groups.length === 0 ? <p className="text-[13px] text-[var(--color-muted)]">No scopes available.</p> : null}
      {groups.map((g) => (
        <fieldset key={g.groupTag} className="mb-3 last:mb-0">
          <legend className="mb-1 text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--color-muted)]">
            {g.groupTag}
          </legend>
          {g.scopes.map((s) => (
            <label key={s.id} className="flex items-center gap-2 py-0.5 text-[13px]">
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-accent)]"
                checked={selected.includes(s.id)}
                onChange={() => toggle(s.id)}
              />
              <span style={mono}>{s.name}</span>
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  )
}

/** Shown once, from component state only. Closing discards it. */
function RawTokenDialog({ minted, onClose }: { minted: MintedToken; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <Dialog
      open
      title="Copy your token now"
      onClose={onClose}
      footer={<Button onClick={onClose}>I've saved it</Button>}
    >
      <p className="text-[14px] text-[var(--color-muted)]">
        This is the only time the token for <strong>{minted.token.name}</strong> is shown. It can't be retrieved later.
      </p>
      <code className="block break-all rounded-[10px] bg-[var(--v1-accent-soft)] p-3 text-[13px]" style={mono}>
        {minted.rawToken}
      </code>
      <div>
        <Button
          variant="quiet"
          onClick={() => {
            navigator.clipboard
              ?.writeText(minted.rawToken)
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }}
        >
          {copied ? 'Copied' : 'Copy to clipboard'}
        </Button>
      </div>
    </Dialog>
  )
}

function CreateDialog({ onClose, onMinted }: { onClose: () => void; onMinted: (m: MintedToken) => void }) {
  const create = useCreateApiToken()
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>([])
  const [expires, setExpires] = useState('')
  return (
    <Dialog
      open
      title="Create an API token"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            disabled={name.trim() === '' || scopes.length === 0 || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  scopes,
                  expiresAt: expires ? new Date(`${expires}T23:59:59Z`).toISOString() : null,
                },
                { onSuccess: onMinted },
              )
            }
          >
            {create.isPending ? 'Creating...' : 'Create token'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Expires (optional)">
        <input className={inputCls} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
      </Field>
      <div>
        <span className="mb-1 block text-[12px] font-medium text-[var(--color-muted)]">Scopes (at least one)</span>
        <ScopePicker selected={scopes} onChange={setScopes} />
      </div>
      {create.error ? <Inline tone="bad">{adminError(create.error)}</Inline> : null}
    </Dialog>
  )
}

function ScopesDialog({ token, onClose }: { token: ApiTokenView; onClose: () => void }) {
  const upd = useUpdateApiTokenScopes()
  const [scopes, setScopes] = useState(token.scopes)
  return (
    <Dialog
      open
      title={`Scopes for ${token.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose} disabled={upd.isPending}>
            Cancel
          </Button>
          <Button
            disabled={scopes.length === 0 || upd.isPending}
            onClick={() => upd.mutate({ id: token.id, scopes }, { onSuccess: onClose })}
          >
            {upd.isPending ? 'Saving...' : 'Save scopes'}
          </Button>
        </>
      }
    >
      <ScopePicker selected={scopes} onChange={setScopes} />
      {upd.error ? <Inline tone="bad">{adminError(upd.error)}</Inline> : null}
    </Dialog>
  )
}

type Pending = { kind: 'rotate' | 'revoke' | 'delete'; token: ApiTokenView }

const statusTone = (s: ApiTokenView['status']) => (s === 'active' ? 'ok' : s === 'expired' ? 'warn' : 'off')
const fmt = (iso: string | null) => (iso ? iso.slice(0, 10) : '-')

export function PorcelainAdminApiTokens() {
  const tokens = useAdminApiTokens()
  const rotate = useRotateApiToken()
  const revoke = useRevokeApiToken()
  const del = useDeleteApiToken()
  const [creating, setCreating] = useState(false)
  const [minted, setMinted] = useState<MintedToken | null>(null)
  const [scopesFor, setScopesFor] = useState<ApiTokenView | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const list = tokens.data ?? []
  const m = pending?.kind === 'rotate' ? rotate : pending?.kind === 'revoke' ? revoke : del
  const close = () => {
    m.reset()
    setPending(null)
  }
  const confirm = () => {
    if (!pending) return
    const { id } = pending.token
    if (pending.kind === 'rotate')
      rotate.mutate(
        { id },
        {
          onSuccess: (r) => {
            setPending(null)
            setMinted(r)
          },
        },
      )
    else (pending.kind === 'revoke' ? revoke : del).mutate({ id }, { onSuccess: () => setPending(null) })
  }
  const copy = {
    rotate: ['Rotate this token?', 'A new secret is issued and the old one stops working immediately.', 'Rotate'],
    revoke: ['Revoke this token?', 'It stops working immediately. You can delete it afterwards.', 'Revoke'],
    delete: ['Delete this token?', 'The revoked token record is permanently removed.', 'Delete'],
  } as const
  return (
    <AdminPage
      title="API tokens"
      subtitle="Machine-to-machine access for services and scripts."
      loading={tokens.isLoading}
      error={!!tokens.error}
      right={<Button onClick={() => setCreating(true)}>Create token</Button>}
    >
      <Card title={`${list.length} token${list.length === 1 ? '' : 's'}`}>
        {list.length === 0 ? (
          <EmptyRow>No API tokens yet.</EmptyRow>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Prefix</th>
                  <th className={th}>Scopes</th>
                  <th className={th}>Status</th>
                  <th className={th}>Expires</th>
                  <th className={th}>Last used</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => {
                  const a = tokenActions(t)
                  return (
                    <tr key={t.id} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                      <td className={`${td} font-medium`}>{t.name}</td>
                      <td className={td} style={mono}>
                        {t.prefixDisplay}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`}>{t.scopes.length}</td>
                      <td className={td}>
                        <Badge label={t.status} tone={statusTone(t.status)} />
                      </td>
                      <td className={td} style={mono}>
                        {fmt(t.expiresAt)}
                      </td>
                      <td className={td} style={mono}>
                        {fmt(t.lastSeenAt)}
                      </td>
                      <td className={`${td} text-right`}>
                        <div className="flex justify-end gap-1">
                          <Button variant="quiet" disabled={t.status === 'revoked'} onClick={() => setScopesFor(t)}>
                            Scopes
                          </Button>
                          {a.canRotate ? (
                            <Button variant="quiet" onClick={() => setPending({ kind: 'rotate', token: t })}>
                              Rotate
                            </Button>
                          ) : null}
                          {a.canRevoke ? (
                            <Button variant="danger" onClick={() => setPending({ kind: 'revoke', token: t })}>
                              Revoke
                            </Button>
                          ) : null}
                          {a.canDelete ? (
                            <Button variant="danger" onClick={() => setPending({ kind: 'delete', token: t })}>
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {creating ? (
        <CreateDialog
          onClose={() => setCreating(false)}
          onMinted={(r) => {
            setCreating(false)
            setMinted(r)
          }}
        />
      ) : null}
      {scopesFor ? <ScopesDialog token={scopesFor} onClose={() => setScopesFor(null)} /> : null}
      {minted ? <RawTokenDialog minted={minted} onClose={() => setMinted(null)} /> : null}
      <ConfirmDialog
        open={pending !== null}
        title={pending ? copy[pending.kind][0] : ''}
        body={pending ? `${pending.token.name}: ${copy[pending.kind][1]}` : null}
        confirmLabel={pending ? copy[pending.kind][2] : ''}
        danger={pending?.kind !== 'rotate'}
        pending={m.isPending}
        error={m.error ? adminError(m.error) : null}
        onConfirm={confirm}
        onClose={close}
      />
    </AdminPage>
  )
}
